import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useContacts, type Contact } from '../../features/contacts/api/useContacts';

// A search-as-you-type contact picker.
//
// The former UX was a plain <select> populated with every contact in the
// system — fine at 20 contacts, unusable at 200. This component opens a
// panel with a search input on focus, debounces to the /contacts?search=
// endpoint the API already supports, and lets the operator pick with the
// keyboard or the finger.
//
// The picker is controlled: parent supplies `value` (contact id or '')
// and `onChange`. Bind with react-hook-form's <Controller>.

interface Props {
  value: string;
  onChange: (id: string) => void;
  /** Narrow the search to a role (some forms only want customers, etc.) */
  filter?: { isCustomer?: boolean; isSupplier?: boolean };
  /** Show the "no contact / walk-in" option — trades allow it, payments don't. */
  allowNone?: boolean;
  /** Optional invalid state hint (react-hook-form error). */
  invalid?: boolean;
  /** DOM id for the visible trigger (label htmlFor). */
  id?: string;
  disabled?: boolean;
}

export function ContactPicker({
  value,
  onChange,
  filter,
  allowNone = false,
  invalid,
  id,
  disabled,
}: Props) {
  const { t } = useTranslation();
  const autoId = useId();
  const triggerId = id ?? `cp-${autoId}`;

  const [open, setOpen] = useState(false);
  const [rawQuery, setRawQuery] = useState('');
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Resolve the currently selected contact's name. Loaded from the same
  // endpoint by pulling the full list once (cached by react-query) —
  // matches how the field worked before, without an extra /contacts/:id
  // roundtrip for a name we already have.
  const preload = useContacts(filter ?? {});
  const selected: Contact | undefined = value
    ? preload.data?.find((c) => c.id === value)
    : undefined;

  // Debounce keystrokes so the endpoint isn't hit on every character.
  useEffect(() => {
    const h = setTimeout(() => setQuery(rawQuery.trim()), 220);
    return () => clearTimeout(h);
  }, [rawQuery]);

  const results = useContacts({ ...(filter ?? {}), ...(query ? { search: query } : {}) });

  // Click outside → close.
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Focus the search input when the panel opens.
  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  const items = useMemo(() => results.data ?? [], [results.data]);

  function pick(cid: string) {
    onChange(cid);
    setOpen(false);
    setRawQuery('');
    setQuery('');
  }

  return (
    <div className="contact-picker" ref={rootRef}>
      <button
        id={triggerId}
        type="button"
        className={`contact-picker__trigger${invalid ? ' is-invalid' : ''}`}
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={`contact-picker__value${selected ? '' : ' is-empty'}`}>
          {selected?.name ?? (allowNone ? t('contact_picker.walkin') : t('contact_picker.choose'))}
        </span>
        <span aria-hidden="true" className="contact-picker__caret">
          ▾
        </span>
      </button>

      {open ? (
        <div className="contact-picker__panel" role="dialog" aria-label={t('contact_picker.title')}>
          <input
            ref={searchRef}
            type="search"
            className="contact-picker__search"
            placeholder={t('contact_picker.search_placeholder')}
            value={rawQuery}
            onChange={(e) => setRawQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setOpen(false);
              if (e.key === 'Enter' && items[0]) {
                e.preventDefault();
                pick(items[0].id);
              }
            }}
          />

          <ul className="contact-picker__list" role="listbox">
            {allowNone ? (
              <li>
                <button
                  type="button"
                  className={`contact-picker__item${value === '' ? ' is-selected' : ''}`}
                  onClick={() => pick('')}
                >
                  <span className="contact-picker__name">{t('contact_picker.walkin')}</span>
                </button>
              </li>
            ) : null}

            {results.isLoading ? (
              <li className="contact-picker__empty">{t('common.loading')}</li>
            ) : items.length === 0 ? (
              <li className="contact-picker__empty">{t('contact_picker.no_results')}</li>
            ) : (
              items.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    className={`contact-picker__item${c.id === value ? ' is-selected' : ''}`}
                    onClick={() => pick(c.id)}
                  >
                    <span className="contact-picker__name">{c.name}</span>
                    {c.phone ? <span className="contact-picker__phone">{c.phone}</span> : null}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
